import { create } from 'zustand';
import { Session } from '@supabase/supabase-js';
import { getFruticReceiverMapping } from 'common/src/api/frutic';
import {
  addReceiverToExporter,
  deleteReceiver,
  getReceiversByExporter,
  updateReceiver,
} from 'common/src/api/exporterReceivers';
import { supabase } from 'common/src/supabase';
import { getShipmentHeaders } from 'common/src/api/shipments';
import {
  ReceiverMapping,
  User,
  ExporterReceiver,
} from 'common/src/utils/types';

type ReceiverStore = {
  mappingLoading: boolean;
  fetchedMappedAndUnmappedReceivers: boolean;
  mappedReceivers: ReceiverMapping[];
  directMappedReceivers: ReceiverMapping[];
  unmappedReceivers: string[];
  exporterReceivers: ExporterReceiver[];
  exporterReceiversLoading: boolean;
  lastExporterId?: number;
  fetchReceiverMappings: (
    speciesId: number,
    season: string,
    session: Session,
    userProfile: User
  ) => Promise<void>;
  setMappedAndUnmappedReceivers: (
    mappedReceivers: ReceiverMapping[],
    unmappedReceivers: string[]
  ) => void;
  saveMapping: (
    fruticReceiverName: string,
    exporterReceiverId: number,
    exporterId: number,
    context?: ReceiverMappingContext
  ) => Promise<void>;
  updateMapping: (
    mappingId: number,
    exporterReceiverId: number,
    context?: ReceiverMappingContext
  ) => Promise<void>;
  deleteMapping: (
    mappingId: number,
    fruticReceiverName: string,
    exporterId?: number,
    context?: ReceiverMappingContext
  ) => Promise<void>;
  fetchExporterReceivers: (
    exporterId: number,
    force?: boolean
  ) => Promise<void>;
  addExporterReceiver: (
    exporterId: number,
    name: string
  ) => Promise<ExporterReceiver>;
  updateExporterReceiverName: (
    id: number,
    name: string
  ) => Promise<ExporterReceiver>;
  deleteExporterReceiver: (id: number) => Promise<void>;
  resetExporterReceivers: () => void;
};

const sortByName = (items: ExporterReceiver[]) =>
  [...items].sort((a, b) => a.name.localeCompare(b.name));

const normalizeReceiverName = (name: string | null | undefined) =>
  (name || '').trim().toUpperCase();

type ReceiverMappingContext = {
  speciesId: number;
  season: string;
  session: Session;
  userProfile: User;
};

type ShipmentReceiverRow = {
  receiver?: string | null;
  exporter_receiver_id?: number | null;
  source_receivers?: ShipmentReceiverRow[];
};

const addUniqueReceiverName = (
  receiversByName: Map<string, string>,
  name: string | null | undefined
) => {
  const normalizedName = normalizeReceiverName(name);
  if (!normalizedName) return;
  if (!receiversByName.has(normalizedName)) {
    receiversByName.set(normalizedName, name || normalizedName);
  }
};

let mappingRequestVersion = 0;
let mappingRequestScope: string | undefined;
const mappingScopeKey = (
  exporterId: number | undefined,
  season: string,
  speciesId: number
) => `${exporterId}:${season}:${speciesId}`;

const useReceiverStore = create<ReceiverStore>((set, get) => ({
  mappingLoading: false,
  fetchedMappedAndUnmappedReceivers: false,
  mappedReceivers: [],
  directMappedReceivers: [],
  unmappedReceivers: [],
  exporterReceivers: [],
  exporterReceiversLoading: false,
  lastExporterId: undefined,
  fetchReceiverMappings: async (speciesId, season, _session, userProfile) => {
    const requestVersion = ++mappingRequestVersion;
    mappingRequestScope = mappingScopeKey(
      userProfile.exporter_id,
      season,
      speciesId
    );
    set({
      mappingLoading: true,
      fetchedMappedAndUnmappedReceivers: false,
      mappedReceivers: [],
      directMappedReceivers: [],
      unmappedReceivers: [],
    });
    try {
      let receiverNames: string[] = [];
      const shipmentReceiverIdsByName = new Map<string, Set<number>>();
      const shipmentNamesWithMissingReceiverId = new Set<string>();

      const shipments = await getShipmentHeaders(
        userProfile.exporter_id!,
        season,
        speciesId
      );
      const receiversByName = new Map<string, string>();

      (shipments as ShipmentReceiverRow[] | null)
        ?.flatMap((shipment) =>
          shipment.source_receivers?.length
            ? shipment.source_receivers
            : [shipment]
        )
        .forEach((shipment) => {
          const normalizedName = normalizeReceiverName(shipment.receiver);
          if (!normalizedName) return;
          addUniqueReceiverName(receiversByName, shipment.receiver);

          if (shipment.exporter_receiver_id) {
            const receiverIds =
              shipmentReceiverIdsByName.get(normalizedName) ||
              new Set<number>();
            receiverIds.add(Number(shipment.exporter_receiver_id));
            shipmentReceiverIdsByName.set(normalizedName, receiverIds);
          } else {
            shipmentNamesWithMissingReceiverId.add(normalizedName);
          }
        });

      receiverNames = Array.from(receiversByName.values());

      const mappings =
        ((await getFruticReceiverMapping(
          userProfile
        )) as unknown as ReceiverMapping[]) || [];
      const exporterReceivers =
        get().lastExporterId === userProfile.exporter_id &&
        get().exporterReceivers.length > 0
          ? get().exporterReceivers
          : await getReceiversByExporter(userProfile.exporter_id);

      const currentReceiverNames = new Set(
        receiverNames.map((name) => normalizeReceiverName(name))
      );
      const allMappedReceivers = new Set(
        mappings?.map((m) => normalizeReceiverName(m.frutic_receiver_name))
      );
      const exporterReceiverById = new Map(
        exporterReceivers.map((receiver) => [receiver.id, receiver])
      );
      const directMappedReceivers = receiverNames
        .map((name) => {
          const normalizedName = normalizeReceiverName(name);
          if (!normalizedName || allMappedReceivers.has(normalizedName)) {
            return null;
          }
          if (shipmentNamesWithMissingReceiverId.has(normalizedName)) {
            return null;
          }
          const shipmentReceiverIds =
            shipmentReceiverIdsByName.get(normalizedName) || new Set<number>();
          const receiver =
            shipmentReceiverIds.size === 1
              ? exporterReceiverById.get(Array.from(shipmentReceiverIds)[0])
              : undefined;

          if (!receiver) return null;

          return {
            id: -receiver.id,
            exporter_id: userProfile.exporter_id!,
            frutic_receiver_name: name,
            exporter_receiver_id: receiver.id,
            exporter_receivers: receiver,
            is_direct_match: true,
            created_at: '',
          } as ReceiverMapping;
        })
        .filter((mapping): mapping is ReceiverMapping => mapping !== null);
      const directMappedReceiverNames = new Set(
        directMappedReceivers.map((m) =>
          normalizeReceiverName(m.frutic_receiver_name)
        )
      );
      const mappedReceivers = mappings?.filter((m) =>
        currentReceiverNames.has(normalizeReceiverName(m.frutic_receiver_name))
      );
      const unmappedReceivers = receiverNames.filter((name: string) => {
        const normalizedName = normalizeReceiverName(name);
        return (
          !allMappedReceivers.has(normalizedName) &&
          !directMappedReceiverNames.has(normalizedName)
        );
      });

      if (requestVersion !== mappingRequestVersion) return;
      set({
        mappedReceivers: mappedReceivers || [],
        directMappedReceivers,
        unmappedReceivers: unmappedReceivers || [],
        fetchedMappedAndUnmappedReceivers: true,
      });
    } catch (error) {
      console.error('Error fetching receiver mappings:', error);
    } finally {
      if (requestVersion === mappingRequestVersion) {
        set({ mappingLoading: false });
      }
    }
  },
  setMappedAndUnmappedReceivers: (mappedReceivers, unmappedReceivers) =>
    set({ mappedReceivers, unmappedReceivers }),
  saveMapping: async (
    fruticReceiverName,
    exporterReceiverId,
    exporterId,
    context
  ) => {
    const scopeKey = context
      ? mappingScopeKey(
          context.userProfile.exporter_id,
          context.season,
          context.speciesId
        )
      : undefined;
    try {
      if (context) {
        const { error } = await supabase.rpc(
          'set_receiver_mapping_for_shipments',
          {
            p_exporter_id: exporterId,
            p_species_id: context.speciesId,
            p_season: context.season,
            p_receiver_name: fruticReceiverName,
            p_exporter_receiver_id: exporterReceiverId,
          }
        );

        if (error) throw error;

        if (scopeKey === mappingRequestScope)
          await get().fetchReceiverMappings(
            context.speciesId,
            context.season,
            context.session,
            context.userProfile
          );
        return;
      }

      const { data: existingMappings, error: checkError } = await supabase
        .from('frutic_receiver_to_exporter_receiver')
        .select('*, exporter_receivers(*)')
        .eq('exporter_id', exporterId);

      if (checkError) throw checkError;

      const normalizedFruticReceiverName =
        normalizeReceiverName(fruticReceiverName);
      const existingMapping = existingMappings?.find(
        (mapping) =>
          normalizeReceiverName(mapping.frutic_receiver_name) ===
          normalizedFruticReceiverName
      );

      if (existingMapping) {
        const { data, error } = await supabase
          .from('frutic_receiver_to_exporter_receiver')
          .update({ exporter_receiver_id: exporterReceiverId })
          .eq('id', existingMapping.id)
          .select('*, exporter_receivers(*)')
          .single();

        if (error) throw error;

        set((state) => ({
          mappedReceivers: state.mappedReceivers.map((m) =>
            m.id === existingMapping.id ? (data as ReceiverMapping) : m
          ),
          unmappedReceivers: state.unmappedReceivers.filter(
            (name) =>
              normalizeReceiverName(name) !== normalizedFruticReceiverName
          ),
        }));
      } else {
        const newMapping = {
          exporter_id: exporterId,
          frutic_receiver_name: fruticReceiverName,
          exporter_receiver_id: exporterReceiverId,
        };

        const { data, error } = await supabase
          .from('frutic_receiver_to_exporter_receiver')
          .insert(newMapping)
          .select('*, exporter_receivers(*)')
          .single();

        if (error) throw error;

        set((state) => ({
          mappedReceivers: [...state.mappedReceivers, data as ReceiverMapping],
          unmappedReceivers: state.unmappedReceivers.filter(
            (name) =>
              normalizeReceiverName(name) !== normalizedFruticReceiverName
          ),
        }));
      }
    } catch (error) {
      console.error('Error saving receiver mapping:', error);
      throw error;
    }
  },
  updateMapping: async (mappingId, exporterReceiverId, context) => {
    try {
      const existingMapping = [
        ...get().mappedReceivers,
        ...get().directMappedReceivers,
      ].find((mapping) => mapping.id === mappingId);

      if (existingMapping) {
        await get().saveMapping(
          existingMapping.frutic_receiver_name,
          exporterReceiverId,
          existingMapping.exporter_id,
          context
        );
        return;
      }

      const { data, error } = await supabase
        .from('frutic_receiver_to_exporter_receiver')
        .update({ exporter_receiver_id: exporterReceiverId })
        .eq('id', mappingId)
        .select('*, exporter_receivers(*)')
        .single();

      if (error) throw error;

      set((state) => ({
        mappedReceivers: state.mappedReceivers.map((m) =>
          m.id === mappingId ? (data as ReceiverMapping) : m
        ),
      }));
    } catch (error) {
      console.error('Error updating receiver mapping:', error);
      throw error;
    }
  },
  deleteMapping: async (mappingId, fruticReceiverName, exporterId, context) => {
    const scopeKey = context
      ? mappingScopeKey(
          context.userProfile.exporter_id,
          context.season,
          context.speciesId
        )
      : undefined;
    try {
      const normalizedFruticReceiverName =
        normalizeReceiverName(fruticReceiverName);
      const existingMapping = [
        ...get().mappedReceivers,
        ...get().directMappedReceivers,
      ].find(
        (mapping) =>
          mapping.id === mappingId ||
          normalizeReceiverName(mapping.frutic_receiver_name) ===
            normalizedFruticReceiverName
      );
      const resolvedExporterId =
        exporterId ||
        existingMapping?.exporter_id ||
        context?.userProfile.exporter_id;
      if (context && resolvedExporterId) {
        const { error } = await supabase.rpc(
          'clear_receiver_mapping_for_shipments',
          {
            p_exporter_id: resolvedExporterId,
            p_species_id: context.speciesId,
            p_season: context.season,
            p_receiver_name: fruticReceiverName,
          }
        );

        if (error) throw error;

        if (scopeKey === mappingRequestScope)
          await get().fetchReceiverMappings(
            context.speciesId,
            context.season,
            context.session,
            context.userProfile
          );
        return;
      }

      const { error } = await supabase
        .from('frutic_receiver_to_exporter_receiver')
        .delete()
        .eq('id', mappingId);

      if (error) throw error;

      set((state) => ({
        mappedReceivers: state.mappedReceivers.filter(
          (m) => m.id !== mappingId
        ),
        unmappedReceivers: state.unmappedReceivers.filter(
          (name) => normalizeReceiverName(name) !== normalizedFruticReceiverName
        ),
      }));

      if (context) {
        if (scopeKey === mappingRequestScope)
          await get().fetchReceiverMappings(
            context.speciesId,
            context.season,
            context.session,
            context.userProfile
          );
      }
    } catch (error) {
      console.error('Error deleting receiver mapping:', error);
      throw error;
    }
  },
  fetchExporterReceivers: async (exporterId, force = false) => {
    const { lastExporterId, exporterReceivers } = get();
    if (
      !force &&
      lastExporterId === exporterId &&
      exporterReceivers.length > 0
    ) {
      return;
    }
    set({ exporterReceiversLoading: true });
    try {
      const data = await getReceiversByExporter(exporterId);
      set({
        exporterReceivers: sortByName(data),
        lastExporterId: exporterId,
      });
    } catch (error) {
      console.error('Error fetching exporter receivers:', error);
      throw error;
    } finally {
      set({ exporterReceiversLoading: false });
    }
  },
  addExporterReceiver: async (exporterId, name) => {
    const created = await addReceiverToExporter(exporterId, name);
    const receiverWithUsers = {
      ...created,
      receiver_access_users: [],
      receiver_access_user_count: 0,
    };
    set((state) => ({
      exporterReceivers: sortByName([
        ...state.exporterReceivers,
        receiverWithUsers,
      ]),
      lastExporterId: exporterId,
    }));
    return receiverWithUsers;
  },
  updateExporterReceiverName: async (id, name) => {
    const updated = await updateReceiver(id, name);
    set((state) => ({
      exporterReceivers: sortByName(
        state.exporterReceivers.map((receiver) =>
          receiver.id === id ? { ...receiver, ...updated } : receiver
        )
      ),
    }));
    return updated;
  },
  deleteExporterReceiver: async (id) => {
    await deleteReceiver(id);
    set((state) => ({
      exporterReceivers: state.exporterReceivers.filter(
        (receiver) => receiver.id !== id
      ),
    }));
  },
  resetExporterReceivers: () =>
    set({
      exporterReceivers: [],
      directMappedReceivers: [],
      lastExporterId: undefined,
    }),
}));

export default useReceiverStore;
